import {
  getAppReaderUrl,
  type NativeReaderRoute,
} from '../packages/content/src/reader-routes'
import { hasNativeQuranBridge } from 'helpers/nativeQuranBridge'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'

type UsePaginationReturnType = {
  page: number
  nextPage: () => void
  previousPage: () => void
  goToPage: (_page: number) => void
}

const usePagination = ({
  path = '',
  max,
  min = 1,
  basePath = '',
  addEventListener = true,
  mobileReader = false,
}: {
  path: string
  max: number
  min?: number
  basePath?: string
  addEventListener?: boolean
  mobileReader?: boolean
}): UsePaginationReturnType => {
  const { query, push } = useRouter()
  const [page, setPage] = useState(Number(query.page) as unknown as number)
  const shouldUseQuranShallowRouting = path === 'kuran'

  /** Navigates to a page while preserving the bundled Quran reader state. */
  const pushPage = (pageToGo: number) => {
    const url = `${basePath}/${path}/${pageToGo}`
    const scrollToTop = () => {
      if (typeof window !== 'undefined') {
        window.scrollTo({ top: 0, left: 0 })
      }
    }

    if (mobileReader) {
      scrollToTop()
      const route: NativeReaderRoute =
        path === 'kuran'
          ? { kind: 'quran', page: pageToGo }
          : path === 'cevsen'
            ? { kind: 'cevsen', page: pageToGo }
            : { kind: 'dua', type: path, page: pageToGo }
      push(getAppReaderUrl(route), undefined, { shallow: true })
      return
    }

    const isOffline = typeof navigator !== 'undefined' && navigator.onLine === false
    const isNativeQuran = shouldUseQuranShallowRouting && hasNativeQuranBridge()
    const shouldUseShallowRouting =
      shouldUseQuranShallowRouting && (isOffline || isNativeQuran)

    if (shouldUseShallowRouting) scrollToTop()

    push(url, undefined, {
      shallow: shouldUseShallowRouting,
    })
  }

  const nextPage = () => {
    setPage((oldPage) => {
      const newPage = Number(oldPage) + 1
      if (Number(oldPage) + 1 > max) {
        return oldPage
      }
      pushPage(newPage)
      return newPage
    })
  }
  const previousPage = () => {
    setPage((oldPage) => {
      if (Number(oldPage) - 1 < min) {
        return oldPage
      }
      const newPage = Number(oldPage) - 1
      pushPage(newPage)
      return newPage
    })
  }

  const goToPage = (pageToGo: number) => {
    setPage(pageToGo)
    pushPage(pageToGo)
  }

  const myEventListener = (event: KeyboardEvent) => {
    if (event.shiftKey || event.altKey || event.ctrlKey || event.metaKey) {
      return
    }

    if (event.key === 'ArrowRight' || event.key === 'MediaTrackNext') {
      nextPage()
    }

    if (event.key === 'ArrowLeft' || event.key === 'MediaTrackPrevious') {
      previousPage()
    }
  }

  useEffect(() => {
    setPage(Number(query.page) as unknown as number)
  }, [query.page])

  useEffect(() => {
    if (addEventListener) {
      document.addEventListener('keyup', myEventListener)
    }
    return () => {
      document.removeEventListener('keyup', myEventListener)
    }
  }, [addEventListener, mobileReader])

  return { page, nextPage, previousPage, goToPage }
}

export default usePagination
